import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, BookOpen, Type, PenTool, Gamepad2, Trophy, Headphones, MessageSquareText } from "lucide-react";
import { api, getChildId } from "../lib/api";
import { loadRecordings, setAudioPrefs, stopAudio } from "../lib/audio";
import { StoryPlayer } from "../components/lesson/StoryPlayer";
import { ZhuyinStep } from "../components/lesson/ZhuyinStep";
import { CharacterStep } from "../components/lesson/CharacterStep";
import { ListenStory, VocabStep, PatternStep } from "../components/lesson/LifeSteps";
import { GAMES } from "../components/lesson/Games";
import { RewardStep } from "../components/lesson/RewardStep";

const ICON = { story: BookOpen, zhuyin: Type, characters: PenTool, activity: Gamepad2, reward: Trophy, listen: Headphones, vocab: Type, patterns: MessageSquareText };

export default function LessonPlayer() {
  const { storyId } = useParams();
  const [sp] = useSearchParams();
  const preview = sp.get("preview") === "1";
  const nav = useNavigate();
  const childId = getChildId();
  const [L, setL] = useState(null);
  const [step, setStep] = useState(0);
  const [prefs, setPrefs] = useState(preview ? {} : null);
  const session = useRef(null);
  const started = useRef(null);
  const firstTry = useRef({});

  useEffect(() => {
    setL(null);
    setStep(0);
    firstTry.current = {};
    api.get(`/lessons/${storyId}${preview ? "?preview=true" : ""}`).then(({ data }) => setL(data)).catch(() => nav("/play"));
    if (!preview && childId) {
      Promise.all([api.get("/children"), loadRecordings()]).then(([{ data }]) => { const s = data.find((c) => c.child_id === childId)?.settings; setAudioPrefs(s); setPrefs(s || {}); });
      if (started.current !== storyId) {
        started.current = storyId;
        api.post("/progress/start", { child_id: childId, lesson_id: storyId }).then(({ data }) => (session.current = data.session_id));
      }
    } else if (!preview) nav("/profiles");
    return () => stopAudio();
  }, [storyId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!L || !prefs) return <div className="min-h-screen paper-bg flex items-center justify-center font-brush text-5xl text-[var(--vermilion)] floaty">載入中…</div>;

  const life = L.story.hall === "life";
  const home = preview ? "/admin" : life ? "/life" : "/play";
  const steps = life ? [
    { kind: "story" }, { kind: "listen" },
    ...(L.vocab.length ? [{ kind: "vocab" }] : []),
    ...(L.patterns.length ? [{ kind: "patterns" }] : []),
    ...L.activities.map((a) => ({ kind: "activity", a })),
    { kind: "reward" },
  ] : [
    { kind: "story" },
    ...(L.zhuyin.length ? [{ kind: "zhuyin" }] : []),
    ...(L.characters.length ? [{ kind: "characters" }] : []),
    ...L.activities.map((a) => ({ kind: "activity", a })),
    { kind: "reward" },
  ];
  const cur = steps[step];
  const live = !preview && childId;

  const goto = (n) => {
    setStep(n);
    if (live && session.current) api.post("/progress/step", { session_id: session.current, step: steps[n]?.kind === "activity" ? steps[n].a.activity_type : steps[n]?.kind }).catch(() => {});
  };
  const next = () => goto(Math.min(step + 1, steps.length - 1));
  const seen = (items) => live && api.post("/progress/seen", { child_id: childId, items }).catch(() => {});
  const traced = (type) => (key) => live && api.post("/progress/attempt", { child_id: childId, lesson_id: storyId, activity_id: `trace_${type}_${key}`, activity_type: "tracing", correct: true, items: [{ type, key }] }).catch(() => {});
  const record = (a) => (correct, items) => live && api.post("/progress/attempt", { child_id: childId, lesson_id: storyId, activity_id: a.activity_id, activity_type: a.activity_type, correct, items: items || a.items || [] }).catch(() => {});
  const actDone = (a) => (first) => { firstTry.current[a.activity_id] = first; next(); };
  const complete = async () => {
    const total = L.activities.length || 1;
    const score = Object.values(firstTry.current).filter(Boolean).length / total;
    if (!live) return { stars: 3, new_badges: [], sticker: L.story.sticker, next_story: null };
    const { data } = await api.post("/progress/complete", { child_id: childId, lesson_id: storyId, session_id: session.current, score });
    return data;
  };
  const replay = () => live && session.current && api.post("/progress/step", { session_id: session.current, step: "story", replay: true }).catch(() => {});

  const Game = cur.kind === "activity" ? GAMES[cur.a.activity_type] : null;

  return (
    <div className={`h-screen flex flex-col ${life ? "life-bg font-round" : "paper-bg"}`} data-testid="lesson-player">
      <header className="flex items-center gap-4 px-4 sm:px-6 py-3">
        <button data-testid="lesson-close-btn" onClick={() => nav(home)} className="w-14 h-14 rounded-full bg-white border-2 border-[var(--line)] flex items-center justify-center shrink-0"><X size={28} /></button>
        <div className={`font-black text-2xl sm:text-3xl shrink-0 hidden sm:block ${life ? "font-round text-[#2c6e65]" : "font-serif-tc text-[var(--vermilion)]"}`}>{L.story.story_title}</div>
        <div className="flex-1 flex gap-1.5 items-center" data-testid="lesson-progress">
          {steps.map((s, k) => {
            const Icon = ICON[s.kind];
            const big = s.kind !== "activity";
            return (
              <div key={k} className={`h-9 rounded-lg flex items-center justify-center transition-all ${big ? "flex-[2]" : "flex-1"} ${k < step ? "bg-[var(--jade)] text-white" : k === step ? "bg-amber-400 text-[var(--ink)]" : "bg-[#efe3cc] text-stone-400"}`}>
                {big && <Icon size={18} />}
              </div>
            );
          })}
        </div>
        {preview && <span className="px-3 py-1 rounded-full bg-amber-200 text-xs font-bold font-en">PREVIEW</span>}
      </header>
      <main className="flex-1 overflow-auto px-4 sm:px-8 pb-6">
        <AnimatePresence mode="wait">
          <motion.div key={step} className="h-full" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }}>
            {cur.kind === "story" && <StoryPlayer title={L.story.story_title} scenes={L.scenes} autoplay={prefs.autoplay_narration !== false} onDone={next} onReplay={replay} />}
            {cur.kind === "zhuyin" && <ZhuyinStep list={L.zhuyin} tones={L.story.tone_focus || []} reviewAll={L.story.zhuyin_review_all} onSeen={seen} onTraced={traced("zhuyin")} onDone={next} />}
            {cur.kind === "characters" && <CharacterStep list={L.characters} onSeen={seen} onTraced={traced("character")} onDone={next} />}
            {cur.kind === "listen" && <ListenStory scenes={L.scenes} title={L.story.story_title} onDone={next} />}
            {cur.kind === "vocab" && <VocabStep list={L.vocab} onSeen={seen} onDone={next} />}
            {cur.kind === "patterns" && <PatternStep list={L.patterns} onSeen={seen} onDone={next} />}
            {Game && <div className="h-full flex items-center justify-center"><Game activity={cur.a} record={record(cur.a)} onDone={actDone(cur.a)} /></div>}
            {cur.kind === "reward" && <RewardStep story={L.story} complete={complete} mapLabel={life ? "回生活館" : "回地圖"} onMap={() => nav(home)} onNext={(id) => nav(`/lesson/${id}`)} />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
